import React from 'react';
import {
  Checkbox,
  CHECKBOX_HORIZONTAL_PADDING,
  DEFAULT_CHECKBOX_SIZE,
} from '@/components/checkbox';
import { createStyles } from '@/contexts/theme-context';
import { EditOrderGuideActions } from '@/hooks/use-edit-order-guide-reducer';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { ListOption } from '@/components/list-item';
import { ParLevelInput } from './par-level-input';
import { Text, View } from 'react-native';
import { useEditOrderGuideActions } from '@/contexts/edit-order-guide-context';
import { useLayoutContext } from '@/contexts/layout-context';

export const LIST_INDEX_WIDTH = 32;

export interface SkuFields {
  itemMetadata: ItemListOptionMetadata;
  itemUUID: string;
}

export type SortableItemType = ListOption<{
  skuFields: SkuFields[];
}>;

export interface ItemState {
  parLevel: number | null;
  selected: boolean;
}

export interface ItemStateMap {
  [itemUUID: string]: ItemState;
}

interface Props {
  index?: number;
  item: SortableItemType;
  itemStateMap: ItemStateMap;
  onSelect: (itemUUID: string) => void;
  partitionUUID: string;
}

// left padding is overridden to 0 for less white space so don't account for
// both sides of horizontal padding in calculation
export const CHECKBOX_CONTAINER_SIZE =
  DEFAULT_CHECKBOX_SIZE + CHECKBOX_HORIZONTAL_PADDING;

// padding reset to 0, so add additional 16px to left side of hitslop
const CHECKBOX_HIT_SLOP = {
  bottom: 0,
  left: 16,
  right: 0,
  top: 0,
};

// TODO: update calculation for large screen
// current designs do not have checkboxes per sku
export const OrderGuideEditableRow: React.FC<Props> = ({
  index,
  item,
  itemStateMap,
  onSelect,
  partitionUUID,
}) => {
  const dispatch = useEditOrderGuideActions();
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();

  return (
    <View style={styles.base} testID={`editable-row-${item.value}`}>
      {isSmallLayout ? null : (
        <Text style={[TYPOGRAPHY.medium, styles.indexText]}>
          {typeof index === 'number' ? index + 1 : ''}
        </Text>
      )}
      <View style={styles.content}>
        <Text numberOfLines={1} style={[TYPOGRAPHY.medium, styles.itemTitle]}>
          {item.title}
        </Text>
        {item.metadata!.skuFields.map((sku) => {
          const itemState = itemStateMap[sku.itemUUID];
          const skuIdentifier = `editable-sku-${sku.itemMetadata.variantPackUUID}`;

          // Render item row after state is hydrated
          return itemState ? (
            <View
              key={skuIdentifier}
              style={styles.skuWrapper}
              testID={skuIdentifier}
            >
              <View style={styles.skuActions}>
                <Checkbox
                  hitSlop={CHECKBOX_HIT_SLOP}
                  isSelected={itemState?.selected ?? false}
                  onSelect={() => onSelect(sku.itemUUID)}
                  style={styles.checkbox}
                  trackingData={{
                    name: 'edit_order_guide.select_item',
                    properties: {
                      variantPackUUID: sku.itemMetadata.variantPackUUID,
                    },
                  }}
                />
                <View collapsable={false}>
                  {sku.itemMetadata.externalID ? (
                    <Text style={[TYPOGRAPHY.listSmall, styles.externalID]}>
                      {sku.itemMetadata.externalID}
                    </Text>
                  ) : null}
                  <Text
                    style={[TYPOGRAPHY.listSubtitle, styles.packDisplayString]}
                  >
                    {sku.itemMetadata.packDisplayString}
                  </Text>
                </View>
              </View>
              <ParLevelInput
                onParLevelChange={(nextValue) => {
                  dispatch({
                    payload: {
                      itemUUID: sku.itemUUID,
                      parLevel: nextValue,
                      partitionUUID,
                    },
                    type: EditOrderGuideActions.SET_ITEM_PAR_LEVEL,
                  });
                }}
                parLevel={itemState.parLevel}
              />
            </View>
          ) : null;
        })}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flexDirection: 'row',
    paddingBottom: 4,
    paddingTop: 8,
  },
  checkbox: {
    paddingLeft: 0,
    paddingVertical: 0,
    width: CHECKBOX_CONTAINER_SIZE,
  },
  content: {
    flex: 1,
  },
  externalID: {
    ...FONTS.primaryBold,
    color: theme.colors.inactiveText,
  },
  indexText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
    width: LIST_INDEX_WIDTH,
  },
  itemTitle: {
    ...FONTS.primarySemiBold,
    marginBottom: 4,
    marginLeft: CHECKBOX_CONTAINER_SIZE,
  },
  packDisplayString: {
    textTransform: 'uppercase',
  },
  skuActions: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  skuWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 4,
  },
}));
